import React, { useState, useEffect, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Info } from "lucide-react";
import PlanRestrictionAlert from "../Chat/ChatTwoColumnPage/PlanRestrictionAlert";
import SSODomainVerification from "./SSODomainVerification";
import SSORedirectUrl from "./SSORedirectUrl";
import SSOIdpLoginUrl from "./SSOIdpLoginUrl";
import SSOProviderInstructions from "./SSOProviderInstructions";
import SSOTestButton from "./SSOTestButton";

const PROVIDER_OPTIONS = [
    { value: "azure_ad", label: "Azure AD" },
    { value: "okta", label: "Okta" },
    { value: "generic_oidc", label: "Generic OIDC" },
];

export default function SSOSettings() {
    const { t } = useTranslation();
    const { isFeatureEnabled, getCustomPlanNumberFeature } = useAuth();
    const ssoFeatureEnabled = isFeatureEnabled("sso");
    const ssoUserLimit = getCustomPlanNumberFeature("ssoUserLimit");
    const isLiteSso = ssoFeatureEnabled && ssoUserLimit < 999999;

    const [enabled, setEnabled] = useState(false);
    const [providerType, setProviderType] = useState("azure_ad");
    const [clientId, setClientId] = useState("");
    const [clientSecret, setClientSecret] = useState("");
    const [issuerUrl, setIssuerUrl] = useState("");
    const [autoCreateUsers, setAutoCreateUsers] = useState(false);
    const [defaultRole, setDefaultRole] = useState("client");
    const [domainVerifications, setDomainVerifications] = useState({});
    const [redirectUrl, setRedirectUrl] = useState("");
    const [idpLoginUrl, setIdpLoginUrl] = useState("");
    const [loaded, setLoaded] = useState(false);

    const { call: getConfig, isLoading: isLoadingConfig } = useBluelagoon({ method: "getSSOConfig" });
    const { call: saveConfig, isLoading: isSaving } = useBluelagoon({ method: "updateSSOConfig" });

    const roleOptions = [
        { value: "admin", label: t("ssoSettings.roleAdmin"), description: t("ssoSettings.roleAdminDesc") },
        { value: "creator", label: t("ssoSettings.roleCreator"), description: t("ssoSettings.roleCreatorDesc") },
        { value: "client", label: t("ssoSettings.roleClient"), description: t("ssoSettings.roleClientDesc") },
    ];

    const loadConfig = useCallback(() => {
        getConfig({}).then((res) => {
            if (res?.config) {
                const c = res.config;
                if (c.providerType) setProviderType(c.providerType);
                if (c.clientId) setClientId(c.clientId);
                if (c.issuerUrl) setIssuerUrl(c.issuerUrl);
                if (c.enabled !== undefined) setEnabled(c.enabled);
                if (c.autoCreateUsers !== undefined) setAutoCreateUsers(c.autoCreateUsers);
                if (c.defaultRole && roleOptions.some((r) => r.value === c.defaultRole)) {
                    setDefaultRole(c.defaultRole);
                }
                setDomainVerifications(c.domainVerifications || {});
                if (c.redirectUrl) setRedirectUrl(c.redirectUrl);
                if (c.idpLoginUrl) setIdpLoginUrl(c.idpLoginUrl);
            }
            setLoaded(true);
        });
    }, [getConfig]);

    useEffect(() => { loadConfig(); }, []);

    const handleSave = async (e) => {
        e.preventDefault();
        if (!ssoFeatureEnabled) return;
        try {
            const payload = { providerType, clientId, issuerUrl, enabled, autoCreateUsers, defaultRole };
            if (clientSecret) payload.clientSecret = clientSecret;
            const res = await saveConfig(payload);
            if (res?.status === 200) {
                toast.success(t("ssoSettings.savedSuccess"));
                setClientSecret("");
            } else {
                toast.error(res?.error || t("ssoSettings.savedError"));
            }
        } catch (err) {
            toast.error(err?.message || t("ssoSettings.savedError"));
        }
    };

    const disabled = !ssoFeatureEnabled;
    if (!loaded && isLoadingConfig) return <div className="p-4">{t("ssoSettings.loading")}</div>;

    const hasConfig = !!(clientId && issuerUrl);

    return (
        <div className="space-y-6 max-w-lg">
            {!ssoFeatureEnabled && (
                <PlanRestrictionAlert variant="inline" titleFallback={t("ssoConfig.upgradeRequired")}
                    descriptionFallback={t("ssoConfig.upgradeDescription")} />
            )}
            {isLiteSso && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    {t("ssoSettings.liteSsoWarning", { limit: ssoUserLimit })}
                </div>
            )}

            <SSOToggle enabled={enabled} onChange={setEnabled} disabled={disabled}
                title={t("ssoSettings.enableSSOAccount")}
                enabledText={t("ssoSettings.enabledAccount")}
                disabledText={t("ssoSettings.disabledAccount")} />

            <form onSubmit={handleSave} className="space-y-4">
                <SectionCard title={t("ssoSettings.sectionProvider")}>
                    <ProviderFields t={t} providerType={providerType} setProviderType={setProviderType}
                        clientId={clientId} setClientId={setClientId} clientSecret={clientSecret}
                        setClientSecret={setClientSecret} issuerUrl={issuerUrl} setIssuerUrl={setIssuerUrl}
                        disabled={disabled} />
                    <SSOProviderInstructions providerType={providerType} redirectUrl={redirectUrl} />
                </SectionCard>

                {hasConfig && (
                    <SectionCard title={t("ssoSettings.sectionUrls")}>
                        <SSORedirectUrl redirectUrl={redirectUrl} />
                        {enabled && idpLoginUrl && (
                            <SSOIdpLoginUrl idpLoginUrl={idpLoginUrl} providerType={providerType} />
                        )}
                    </SectionCard>
                )}

                <SectionCard title={t("ssoSettings.sectionProvisioning")}>
                    <SSOToggle enabled={autoCreateUsers} onChange={setAutoCreateUsers} disabled={disabled}
                        title={t("ssoSettings.autoCreateUsers")}
                        enabledText={t("ssoSettings.autoCreateEnabled")}
                        disabledText={t("ssoSettings.autoCreateDisabled")} />
                    {autoCreateUsers && (
                        <Field label={t("ssoSettings.defaultRole")}>
                            <select value={defaultRole} onChange={(e) => setDefaultRole(e.target.value)} disabled={disabled}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:opacity-50">
                                {roleOptions.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                                {roleOptions.find((r) => r.value === defaultRole)?.description}
                            </p>
                        </Field>
                    )}
                    <GroupsSetupHelp t={t} />
                </SectionCard>

                <div className="flex gap-2">
                    <button type="submit" disabled={disabled || isSaving || !clientId || !issuerUrl}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
                        {isSaving ? t("ssoSettings.saving") : t("ssoSettings.saveConfig")}
                    </button>
                    <SSOTestButton disabled={disabled || !enabled} />
                </div>
            </form>

            <SSODomainVerification domainVerifications={domainVerifications} disabled={disabled} onUpdate={loadConfig} />
        </div>
    );
}

function GroupsSetupHelp({ t }) {
    const [open, setOpen] = useState(false);
    return (
        <div>
            <button type="button" onClick={() => setOpen(!open)}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                <Info className="w-3.5 h-3.5" />
                {t("ssoConfig.groupsSetupTitle")}
            </button>
            {open && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-gray-700 space-y-2">
                    <p>{t("ssoConfig.groupsSetupDescription")}</p>
                    <ul className="list-disc pl-4 space-y-1">
                        <li><strong>Cognito:</strong> {t("ssoConfig.cognitoSetup")}</li>
                        <li><strong>Okta:</strong> {t("ssoConfig.oktaSetup")}</li>
                        <li><strong>Azure AD:</strong> {t("ssoConfig.azureSetup")}</li>
                        <li>{t("ssoConfig.genericSetup")}</li>
                    </ul>
                </div>
            )}
        </div>
    );
}

function ProviderFields({ t, providerType, setProviderType, clientId, setClientId, clientSecret, setClientSecret, issuerUrl, setIssuerUrl, disabled }) {
    return (
        <>
            <Field label={t("ssoSettings.provider")}>
                <select value={providerType} onChange={(e) => setProviderType(e.target.value)} disabled={disabled}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:opacity-50">
                    {PROVIDER_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
            </Field>
            <Field label={t("ssoSettings.clientId")}>
                <input type="text" value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={disabled}
                    placeholder={t("ssoSettings.clientIdPlaceholder")} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:opacity-50" />
            </Field>
            <Field label={t("ssoSettings.clientSecret")}>
                <input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} disabled={disabled}
                    placeholder={t("ssoSettings.clientSecretPlaceholder")} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:opacity-50" />
            </Field>
            <Field label={t("ssoSettings.issuerUrl")}>
                <input type="url" value={issuerUrl} onChange={(e) => setIssuerUrl(e.target.value)} disabled={disabled}
                    placeholder={t("ssoSettings.issuerUrlPlaceholder")}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:opacity-50" />
            </Field>
        </>
    );
}

function SectionCard({ title, children }) {
    return (
        <div className="border border-gray-200 rounded-lg p-4 space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            {children}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            {children}
        </div>
    );
}

function SSOToggle({ enabled, onChange, disabled, title, enabledText, disabledText }) {
    return (
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
                <h3 className="text-sm font-medium text-gray-900">{title}</h3>
                <p className="text-sm text-gray-500 mt-1">{enabled ? enabledText : disabledText}</p>
            </div>
            <button type="button" onClick={() => onChange(!enabled)} disabled={disabled}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? "bg-blue-600" : "bg-gray-300"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
            </button>
        </div>
    );
}
